import { useQueryClient } from "@tanstack/react-query";
import type { LLMModelsResponse } from "@/api/models/api-types";

interface UsePrefetchOptions {
  limit?: number;
  searchTerm?: string;
}

export const useAIModelPrefetch = () => {
  const queryClient = useQueryClient();

  const prefetchModels = async (options: UsePrefetchOptions = {}) => {
    const { limit = 20, searchTerm = "" } = options;

    await queryClient.prefetchInfiniteQuery({
      queryKey: [
        "llm-models",
        { provider_type: "generative", limit, searchTerm },
      ],
      queryFn: async ({ pageParam = 1 }): Promise<LLMModelsResponse> => {
        const searchParams = new URLSearchParams({
          page: String(pageParam),
          limit: String(limit),
          provider_type: "generative",
        });

        if (searchTerm.trim()) {
          // Backend gereksinimi: query param adı 'search'
          searchParams.append("search", searchTerm.trim());
        }

        const { apiClient } = await import("@/api/config/api-client");
        const { API_ENDPOINTS } = await import("@/api/config/api-endpoints");

        const response = await apiClient.get<LLMModelsResponse>(
          `${API_ENDPOINTS.getAllLLMModels}?${searchParams.toString()}`
        );

        return response;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage: LLMModelsResponse) => {
        const { page, totalPage } = lastPage.data.meta;
        const nextPage = page + 1;
        return nextPage <= totalPage ? nextPage : undefined;
      },
      staleTime: 5 * 60 * 1000,
    });
  };

  return {
    prefetchModels,
    prefetchAIModels: prefetchModels, // Alias for backward compatibility
  };
};
